import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { ChevronDown, Save } from "lucide-react";
import FloatingNumber from "../../../components/input/FloatingNumber";
import CustomSelect from "../../../components/customselect/CustomSelect";
import { formatCurrency, sanitizeNumberInput } from "../../../utils/format";

const Accordion = ({
  costGroups,
  template,
  templateKeyMap,
  theme,
  updateFee,
  saveSection,
  showCommissionSection,
  onDownload,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const itemRefs = useRef([]);

  // ✅ Initialize React Hook Form here
  const { control } = useForm({
    defaultValues: {}, // you can prefill values here if needed
  });

  // ✅ Auto-scroll on open
  useEffect(() => {
    if (activeIndex !== null && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeIndex]);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <>
      <div className="ac-content-wrap">
        <div className="download-content">
          <div className="note-card">
            <div className="note-download">
              <h3>Note</h3>
              <p className="heading-b">
                If you are redownloading default costs, put a “check” in the Lock boxes that you do not want to be changed before clicking on the “Redownload default costs” tab.
              </p>
            </div>
            <div className="download-btn">
              <button onClick={() => onDownload && onDownload()}>
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="accordion-setting">
          {costGroups.map((group, index) => {
            const isActive = activeIndex === index;

            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`accordion-item ${isActive ? "active" : ""}`}
              >
                {/* Accordion Title */}
                <button
                  className={`accordion-title heading-d ${isActive ? "open" : ""}`}
                  onClick={() => toggleAccordion(index)}
                >
                  {group.title}
                  <span className="arrow">
                    <ChevronDown
                      className={`lucide lucide-chevron-down transition-transform ${isActive ? "rotate-180" : ""
                        }`}
                      aria-hidden="true"
                    />
                  </span>
                </button>

                {/* Accordion Content */}
                <div
                  className={`accordion-content ${isActive ? "open" : "closed"
                    } transition-all duration-500 ease-in-out`}
                  style={{
                    maxHeight: isActive ? "none" : 0,
                    overflow: isActive ? "visible" : "hidden",
                  }}
                >
                  <div className="accordian-container-inner wrap-full-half">
                    {templateKeyMap[group.title]?.map(
                      (sectionKey) =>
                        template[sectionKey] && (


                          <div
                            key={sectionKey}
                            className={`accordion-datatable application-fe tbl-type-c mb-6
                                ${sectionKey === "buyer_closing_costs_section"
                                ? "width-full"
                                : sectionKey === "seller_closing_costs_section1" || sectionKey === "shortsale_closing_costs_section1" || sectionKey === "refinance_closing_costs_section1"
                                  ? "width-first"
                                  : sectionKey === "seller_closing_costs_section2" || sectionKey === "shortsale_closing_costs_section2" || sectionKey === "refinance_closing_costs_section2"
                                    ? "width-second"
                                    : "width-half"}
  `}
                          >
                            <div className="acc-container-header">
                              <h3>
                                {sectionKey
                                  .replace(/_section[0-9]*$/i, "") // ✅ remove trailing "_section1" or "_section2"
                                  .replace(/_/g, " ")              // ✅ replace underscores with spaces
                                  .replace(/\b\w/g, (c) => c.toUpperCase())}  {/* ✅ capitalize each word */}
                              </h3>

                              <button
                                className="submit-btn btn-bg inpt-btn"
                                onClick={() => saveSection(sectionKey)}
                              >
                                <Save size={14} /> Save
                              </button>
                            </div>

                            <div className="tbl-container">

                              <table>
                                <thead>
                                  <tr>
                                    <th className="heading-c text-left border">Label</th>
                                    {[
                                      "seller_closing_costs_section2",
                                      "shortsale_closing_costs_section2",
                                      "buyer_closing_costs_section",
                                      "refinance_closing_costs_section2",
                                    ].includes(sectionKey) && (
                                        <th className="heading-c text-left" style={{ width: "130px" }}>Type</th>
                                      )}

                                    <th className="heading-c text-center" style={{ width: "132px" }}>Amount</th>
                                    {[
                                      "fha_closing_costs",
                                      "usda_closing_costs",
                                      "va_closing_costs",
                                    ].includes(sectionKey) && (
                                        <th className="heading-c text-left" style={{ width: "120px" }}>Payer</th>
                                      )}
                                    {(sectionKey === "buyer_closing_costs_section" ||
                                      sectionKey === "buyer_payments_section") && (
                                        <th className="heading-c text-center" style={{ width: "50px" }}>Cash</th>
                                      )}

                                    {/*}
                                {(sectionKey === "buyer_closing_costs_section" ||
                                  sectionKey === "seller_closing_costs_section2" ||
                                  sectionKey === "shortsale_closing_costs_section2") && (
                                    <th className="heading-c text-left" style={{ width: "120px" }}>Cash</th>
                                  )}
                                    */}

                                    <th className="heading-c text-center" style={{ width: "50px" }}>Lock</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {template[sectionKey].map((fee, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? "" : ""}>
                                      {/* Label */}
                                      <td>
                                        <div className="form-group-wrap">
                                          <div className="form-group">

                                            {(
                                              sectionKey === "buyer_tenure_section" ||
                                              sectionKey === "buyer_taxes_insurance_section" ||
                                              sectionKey === "seller_closing_costs_section1" ||
                                              sectionKey === "shortsale_closing_costs_section1" ||
                                              sectionKey === "fha_closing_costs" ||
                                              sectionKey === "va_closing_costs" ||
                                              sectionKey === "usda_closing_costs" ||
                                              sectionKey === "refinance_closing_costs_section1" ||
                                              sectionKey === "conventional_adjustable_section" ||
                                              sectionKey === "conventional_closing_costs_section"
                                            ) ? (
                                              <input
                                                type="text"
                                                value={fee.label || ""}
                                                onChange={(e) => updateFee(sectionKey, idx, "label", e.target.value)}
                                                disabled
                                              />
                                            ) : (
                                              <input
                                                type="text"
                                                value={fee.label || ""}
                                                onChange={(e) => updateFee(sectionKey, idx, "label", e.target.value)}
                                              />
                                            )}

                                          </div>
                                          {["seller_closing_costs_section1", "shortsale_closing_costs_section1", "buyer_payments_section"].includes(sectionKey) &&
                                            fee.rate !== undefined && (
                                              <div className="form-group sign-of-percentage tbl-clmn-width-70">
                                                <FloatingNumber
                                                  type="text"
                                                  value={fee.rate || "0"}
                                                  onChange={(e) => updateFee(sectionKey, idx, "rate", e.target.value)}

                                                /></div>
                                            )}

                                        </div>
                                      </td>


                                      {/* Type / Payer / Blank Column */}
                                      {[
                                        "seller_closing_costs_section2",
                                        "shortsale_closing_costs_section2",
                                        "buyer_closing_costs_section",
                                        "refinance_closing_costs_section2",
                                      ].includes(sectionKey) && fee.type !== undefined && (
                                          <td style={{ width: "130px" }}>
                                            <div className="form-group">
                                              <Controller
                                                name={`${sectionKey}.${idx}.type`}
                                                control={control}
                                                defaultValue={fee.type}
                                                render={({ field }) => {
                                                  const val = field.value || fee.type;

                                                  return (
                                                    <CustomSelect
                                                      {...field}
                                                      value={val ? { label: val, value: val } : null}
                                                      placeholder={fee.type}
                                                      options={[
                                                        { label: "Flat Fee", value: "Flat Fee" },
                                                        { label: "% Sales Price", value: "% Sales Price" },
                                                        { label: "% Loan", value: "% Loan" },
                                                      ]}
                                                      onChange={(selected) => {
                                                        const value = selected?.value || selected;   // ← FIX
                                                        field.onChange(value);
                                                        updateFee(sectionKey, idx, "type", value);
                                                      }}
                                                    />
                                                  );
                                                }}
                                              />

                                            </div>
                                          </td>
                                        )}

                                      {/* Amount */}
                                      <td style={{ width: "132px" }}>
                                        <div className="form-group text-right dollr-w-input">
                                          {[
                                            "buyer_closing_costs_section",
                                            "seller_closing_costs_section2",
                                            "shortsale_closing_costs_section2",
                                            "refinance_closing_costs_section2",
                                          ].includes(sectionKey) && fee.direction === "CREDIT" && (
                                              <span
                                                className="sign-indicator"
                                                title="Credit — subtracts from the total"
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  fontSize: "12px",
                                                  fontWeight: 700,
                                                  color: "#1f9254",
                                                }}
                                              >
                                                −
                                              </span>
                                            )}
                                          <span>{fee.unit}</span>
                                          <FloatingNumber
                                            type="text"
                                            decimals={
                                              fee.value_type === "rate" ? 4 :
                                                fee.value_type === "month" || fee.value_type === "year" ? 0 : 2
                                            }
                                            value={
                                              fee.value_type === "rate" ||
                                                fee.value_type === "month" ||
                                                fee.value_type === "year"
                                                ? fee.value ?? ""
                                                : fee.value !== undefined && fee.value !== null
                                                  ? fee.value
                                                  : "0.00"
                                            }
                                            //onChange={(e) => updateFee(sectionKey, idx, "value", sanitizeNumberInput(e.target.value))}
                                            onChange={(e) => {
                                              let decimals = 2;

                                              if (fee.value_type === "rate") decimals = 4;
                                              else if (fee.value_type === "month" || fee.value_type === "year") decimals = 0;

                                              updateFee(
                                                sectionKey,
                                                idx,
                                                "value",
                                                sanitizeNumberInput(e.target.value, fee.value, decimals)
                                              );
                                            }}
                                            onBlur={(e) => {
                                              if (
                                                fee.value_type !== "rate" &&
                                                fee.value_type !== "month" &&
                                                fee.value_type !== "year"
                                              ) {
                                                updateFee(sectionKey, idx, "value", formatCurrency(e.target.value));
                                              }
                                            }}

                                          />
                                        </div>
                                      </td>

                                      {[
                                        "fha_closing_costs",
                                        "usda_closing_costs",
                                        "va_closing_costs",
                                      ].includes(sectionKey) && fee.payer !== undefined && fee.payer !== "factor" && fee.label !== "Origination Factor" && (
                                          <td style={{ width: "120px" }}>
                                            <div className="form-group">
                                              <Controller
                                                name={`${sectionKey}.${idx}.payer`}          // unique field path for RHF
                                                control={control}
                                                defaultValue={fee.payer || "Buyer"} // sets initial value
                                                render={({ field }) => (
                                                  <CustomSelect
                                                    {...field}
                                                    placeholder={field.value}
                                                    value={field.value}
                                                    onChange={(value) => {
                                                      field.onChange(value);                     // updates React Hook Form
                                                      updateFee(sectionKey, idx, "payer", value); // keeps local state in sync
                                                    }}
                                                    options={["Buyer", "Seller"]}                 // uses simple array options
                                                  />
                                                )}
                                              />
                                            </div>
                                          </td>
                                        )}

                                      {[
                                        "fha_closing_costs",
                                        "usda_closing_costs",
                                        "va_closing_costs",
                                      ].includes(sectionKey) && fee.payer !== undefined && fee.payer === "factor" && (
                                          <td></td>
                                        )}


                                      {/* Apply checkbox */}
                                      {(sectionKey === "buyer_closing_costs_section" ||
                                        sectionKey === "buyer_payments_section") && (
                                          <td style={{ width: "60px" }}>
                                            <div className="form-group ">
                                              {(sectionKey === "buyer_closing_costs_section" || fee.apply !== undefined) && (
                                                <input
                                                  type="checkbox"
                                                  checked={fee.apply === "Y"}
                                                  onChange={(e) =>
                                                    updateFee(sectionKey, idx, "apply", e.target.checked ? "Y" : "N")
                                                  }
                                                />
                                              )}
                                            </div>
                                          </td>
                                        )}

                                      {/*
                                  {(sectionKey === "buyer_closing_costs_section" ||
                                    sectionKey === "seller_closing_costs_section2" ||
                                    sectionKey === "shortsale_closing_costs_section2") && (
                                      <td style={{ width: "100px" }}>
                                        <div className="form-group text-right dollr-w-input">
                                          <span>$</span>
                                          <FloatingNumber
                                            type="text"
                                            value={
                                              fee.value_type === "rate" ||
                                                fee.value_type === "month" ||
                                                fee.value_type === "year"
                                                ? fee.Cash ?? ""
                                                : fee.Cash !== undefined && fee.Cash !== null
                                                  ? fee.Cash
                                                  : "0.00"
                                            }
                                            onChange={(e) => updateFee(sectionKey, idx, "Cash", sanitizeNumberInput(e.target.value))}
                                            onBlur={(e) => {
                                              if (
                                                fee.value_type !== "rate" &&
                                                fee.value_type !== "month" &&
                                                fee.value_type !== "year"
                                              ) {
                                                updateFee(sectionKey, idx, "Cash", formatCurrency(e.target.value));
                                              }
                                            }}

                                          />
                                        </div>
                                      </td>
                                    )}
                                      */}


                                      {/* Lock */}
                                      <td style={{ width: "50px" }}>
                                        <div className="form-group ">
                                          <input
                                            type="checkbox"
                                            checked={fee.lock === "Y"}
                                            onChange={(e) =>
                                              updateFee(sectionKey, idx, "lock", e.target.checked ? "Y" : "N")
                                            }
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>


                              </table>

                            </div>

                          </div>
                        )
                    )}

                    {/* ✅ Seller Costs → Commissions Table */}
                    {showCommissionSection &&
                      group.title === "Seller Costs" &&
                      template?.seller_othercosts?.payments_to_buyer && (
                        <div className="accordion-datatable application-fe tbl-type-c width-full">
                          <div className="acc-container-header">
                            <h3>Commissions - Payments to Buyer</h3>
                            <button
                              className="submit-btn btn-bg inpt-btn"
                              onClick={() =>
                                saveSection("seller_othercosts", "payments_to_buyer")
                              }
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-save" aria-hidden="true"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"></path><path d="M7 3v4a1 1 0 0 0 1 1h7"></path></svg> Save
                            </button>
                          </div>

                          <div className="tbl-container">
                            <table>
                              <thead>
                                <tr>
                                  <th className="heading-c">Label</th>
                                  <th className="heading-c text-center" style={{ width: "120px" }}>Rate (%)</th>
                                  <th className="heading-c text-right" style={{ width: "120px" }}>Amount ($)</th>
                                  <th className="heading-c text-center" style={{ width: "60px" }}>Lock</th>
                                </tr>
                              </thead>
                              <tbody>
                                {template.seller_othercosts.payments_to_buyer
                                  .filter((fee) => fee.key !== "days_interest")
                                  .map((fee, idx) => (
                                    <tr key={idx}>
                                      {/* Label */}
                                      <td>
                                        <div className="form-group">
                                          <input
                                            type="text"
                                            value={fee.label || ""}
                                            onChange={(e) =>
                                              updateFee(
                                                "seller_othercosts",
                                                idx,
                                                "label",
                                                e.target.value,
                                                "payments_to_buyer"
                                              )
                                            }
                                            className="inpt"
                                            disabled
                                          />
                                        </div>
                                      </td>

                                      {/* Rate */}
                                      <td style={{ width: "120px" }}>
                                        <div className="form-group dollr-w-input">
                                          <FloatingNumber
                                            type="text"
                                            value={fee.rate || ""}
                                            onChange={(e) =>
                                              updateFee(
                                                "seller_othercosts",
                                                idx,
                                                "rate",
                                                sanitizeNumberInput(e.target.value, "", 4),
                                                "payments_to_buyer"
                                              )
                                            }

                                          />
                                          <span>%</span>
                                        </div>
                                      </td>

                                      {/* Amount */}
                                      <td style={{ width: "120px" }}>
                                        <div className="form-group dollr-w-input">
                                          <span>
                                            {fee.unit || "$"}
                                          </span>
                                          <FloatingNumber
                                            type="text"
                                            value={fee.value || ""}
                                            onChange={(e) =>
                                              updateFee(
                                                "seller_othercosts",
                                                idx,
                                                "value",
                                                sanitizeNumberInput(e.target.value),
                                                "payments_to_buyer"
                                              )
                                            }
                                            onBlur={(e) =>
                                              updateFee(
                                                "seller_othercosts",
                                                idx,
                                                "value",
                                                formatCurrency(e.target.value),
                                                "payments_to_buyer"
                                              )
                                            }

                                          />
                                        </div>
                                      </td>

                                      {/* Lock */}
                                      <td className="text-center" style={{ width: "60px" }}>
                                        <div className="form-group">
                                          <input
                                            type="checkbox"
                                            checked={fee.lock === "Y"}
                                            onChange={(e) =>
                                              updateFee(
                                                "seller_othercosts",
                                                idx,
                                                "lock",
                                                e.target.checked ? "Y" : "N",
                                                "payments_to_buyer"
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Accordion;
